/**
 * Property Revenue Estimator
 * 
 * Calculates estimated revenue for short-term rental properties
 * based on property characteristics, location, and market conditions.
 */

/**
 * Calculate property revenue estimate
 * @param {Object} data - Property and form data
 * @returns {Object} Estimate with ADR, occupancy, and revenue ranges
 */
export const calculateEstimate = (data) => {
  const {
    propertyType,
    sqMeters,
    rooms,
    bathrooms,
    guests,
    periodType,
    services = [],
    condition = 'ready',
    coordinates
  } = data;

  // Step 1: Calculate base ADR (Average Daily Rate)
  const baseAdr = getBaseAdr(propertyType);
  
  // Step 2: Apply adjustments
  const adjustments = [];
  let finalAdr = baseAdr;

  // Size adjustment
  if (sqMeters > 50) {
    const sizeAdjustment = Math.min((sqMeters - 50) * 0.25, 40);
    adjustments.push({
      reason: `Metri quadri (${sqMeters}m²)`,
      amount: sizeAdjustment
    });
    finalAdr += sizeAdjustment;
  }

  // Rooms adjustment
  if (rooms > 1) {
    const roomsAdjustment = Math.min((rooms - 1) * 10, 40);
    adjustments.push({
      reason: `Camere extra (${rooms - 1})`,
      amount: roomsAdjustment
    });
    finalAdr += roomsAdjustment;
  }

  // Bathrooms adjustment
  if (bathrooms > 1) {
    const bathroomsAdjustment = Math.min((bathrooms - 1) * 6, 24);
    adjustments.push({
      reason: `Bagni extra (${bathrooms - 1})`,
      amount: bathroomsAdjustment
    });
    finalAdr += bathroomsAdjustment;
  }

  // Guests adjustment
  if (guests > 2) {
    const guestsAdjustment = Math.min((guests - 2) * 4, 40);
    adjustments.push({
      reason: `Ospiti extra (${guests - 2})`,
      amount: guestsAdjustment
    });
    finalAdr += guestsAdjustment;
  }

  // Services adjustments
  const serviceAdjustments = calculateServiceAdjustments(services);
  serviceAdjustments.forEach(adj => {
    adjustments.push(adj);
    finalAdr += adj.amount;
  });

  // Condition adjustment
  const conditionAdjustment = getConditionAdjustment(condition, finalAdr);
  if (conditionAdjustment.amount !== 0) {
    adjustments.push(conditionAdjustment);
    finalAdr += conditionAdjustment.amount;
  }

  // Location adjustment (basic implementation)
  const locationAdjustment = getLocationAdjustment(coordinates);
  if (locationAdjustment.amount !== 0) {
    adjustments.push(locationAdjustment);
    finalAdr += locationAdjustment.amount;
  }

  // Step 3: Calculate occupancy rate
  const occupancy = getOccupancyRate(propertyType, periodType);

  // Step 4: Calculate revenue ranges (±15%)
  const minAdr = finalAdr * 0.85;
  const maxAdr = finalAdr * 1.15;

  const monthlyMin = Math.round(minAdr * 30 * occupancy);
  const monthlyMax = Math.round(maxAdr * 30 * occupancy);
  const annualMin = Math.round(minAdr * 365 * occupancy);
  const annualMax = Math.round(maxAdr * 365 * occupancy);

  // Breakdown for transparency
  const breakdown = {
    baseAdr,
    adjustments,
    finalAdr: Math.round(finalAdr),
    occupancy: Math.round(occupancy * 100),
    seasonal: periodType === 'seasonal' ? {
      high: 75, // High season occupancy %
      medium: 55, // Medium season occupancy %
      low: 35 // Low season occupancy %
    } : null
  };

  return {
    adrBase: Math.round(finalAdr),
    occ: occupancy,
    monthlyMin,
    monthlyMax,
    annualMin,
    annualMax,
    breakdown
  };
};

/**
 * Get base ADR by property type
 */
const getBaseAdr = (propertyType) => {
  const baseRates = {
    apartment: 70,
    studio: 55,
    loft: 80,
    'holiday-home': 85,
    bnb: 90,
    agriturismo: 95,
    villa: 160
  };

  return baseRates[propertyType] || 70;
};

/**
 * Calculate service-based adjustments
 */
const calculateServiceAdjustments = (services) => {
  const serviceValues = {
    pool: 35,
    jacuzzi: 20,
    balcony: 8,
    garden: 10,
    parking: 6,
    garage: 6,
    gym: 8,
    ac: 6,
    view: 10,
    elevator: 4,
    pets: 4,
    smartlock: 4
  };

  return services.map(service => ({
    reason: getServiceLabel(service),
    amount: serviceValues[service] || 0
  })).filter(adj => adj.amount > 0);
};

/**
 * Get human-readable service labels
 */
const getServiceLabel = (service) => {
  const labels = {
    pool: 'Piscina',
    jacuzzi: 'Jacuzzi',
    balcony: 'Balcone/Terrazza',
    garden: 'Giardino/Corte',
    parking: 'Posto auto',
    garage: 'Garage',
    gym: 'Palestra',
    ac: 'Aria condizionata',
    view: 'Vista panoramica',
    elevator: 'Ascensore',
    pets: 'Animali ammessi',
    smartlock: 'Smart lock'
  };

  return labels[service] || service;
};

/**
 * Calculate condition-based adjustment
 */
const getConditionAdjustment = (condition, currentAdr) => {
  const conditionMultipliers = {
    ready: 0,
    furnish: -0.10, // -10%
    renovate: -0.20, // -20%
    buying: -0.05 // -5%
  };

  const multiplier = conditionMultipliers[condition] || 0;
  const amount = Math.round(currentAdr * multiplier);

  const labels = {
    ready: 'Pronta all\'uso',
    furnish: 'Da arredare',
    renovate: 'Da ristrutturare',
    buying: 'In acquisto'
  };

  return {
    reason: labels[condition] || condition,
    amount
  };
};

/**
 * Basic location-based adjustment
 * This is a simplified implementation - in production you'd use
 * more sophisticated location data and market analysis
 */
const getLocationAdjustment = (coordinates) => {
  if (!coordinates) {
    return { reason: 'Posizione non specificata', amount: 0 };
  }

  const { lat, lng } = coordinates;

  // Major Italian cities (approximate coordinates)
  const majorCities = [
    { name: 'Milano', lat: 45.4642, lng: 9.1900, bonus: 15 },
    { name: 'Roma', lat: 41.9028, lng: 12.4964, bonus: 12 },
    { name: 'Firenze', lat: 43.7696, lng: 11.2558, bonus: 18 },
    { name: 'Venezia', lat: 45.4408, lng: 12.3155, bonus: 20 },
    { name: 'Napoli', lat: 40.8518, lng: 14.2681, bonus: 8 },
    { name: 'Torino', lat: 45.0703, lng: 7.6869, bonus: 10 },
    { name: 'Bologna', lat: 44.4949, lng: 11.3426, bonus: 12 }
  ];

  // Check if property is near a major city (within ~50km)
  for (const city of majorCities) {
    const distance = calculateDistance(lat, lng, city.lat, city.lng);
    if (distance < 50) { // Within 50km
      const proximityFactor = Math.max(0, 1 - (distance / 50));
      const adjustment = Math.round(city.bonus * proximityFactor);
      
      if (adjustment > 0) {
        return {
          reason: `Vicinanza a ${city.name}`,
          amount: adjustment
        };
      }
    }
  }

  // Coastal areas bonus (simplified check for Italian coastline)
  const isCoastal = (
    (lat > 36 && lat < 47 && lng > 6 && lng < 19) && // Italian territory
    (lng < 8 || lng > 15 || lat < 40) // Likely coastal areas
  );

  if (isCoastal) {
    return {
      reason: 'Zona costiera',
      amount: 8
    };
  }

  return { reason: 'Posizione standard', amount: 0 };
};

/**
 * Calculate distance between two coordinates (Haversine formula)
 */
const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371; // Earth's radius in km
  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const toRadians = (degrees) => degrees * (Math.PI / 180);

/**
 * Calculate occupancy rate based on property type and period
 */
const getOccupancyRate = (propertyType, periodType) => {
  // Base occupancy rates for annual rentals
  const annualOccupancy = {
    apartment: 0.58,
    bnb: 0.65,
    agriturismo: 0.62,
    studio: 0.68,
    loft: 0.60,
    'holiday-home': 0.60,
    villa: 0.55
  };

  const baseOccupancy = annualOccupancy[propertyType] || 0.58;

  if (periodType === 'seasonal') {
    // For seasonal rentals, calculate weighted average
    // Assuming 4 months high (75%), 4 months medium (55%), 4 months low (35%)
    const seasonalAverage = (0.75 * 4 + 0.55 * 4 + 0.35 * 4) / 12;
    return seasonalAverage;
  }

  return baseOccupancy;
};